import type { CountriesAlpha2, State } from '@sequencehq/api/commonEnums'
import {
  dashboardv99990101Client,
  type Dashboardv99990101CommonModels
} from '@sequencehq/api/dashboard/v99990101'
import { useLoadAllItemsQuery } from '@sequencehq/api/utils'
import { type ComponentProps, useMemo } from 'react'

import { useTaxIntegrationProvider } from 'lib/hooks/useTaxIntegrationProvider.ts'
import { useCreateTaxRate } from 'SettingsV2/components/tax-rates/hooks/useCreateTaxRate'
import { ControlledTaxRatesModal } from 'SettingsV2/components/tax-rates/modals/TaxRatesModal'
import type { TaxRate } from 'SettingsV2/components/tax-rates/taxRates.types'
import { getMissingTaxRateMessage } from 'SettingsV2/components/tax-rates/taxRates.utils'

import type { ValidationItem } from './useSteppedModal'

export type TaxValidationTaxCategory = Pick<
  Dashboardv99990101CommonModels.TaxCategory.TaxCategory,
  'id' | 'name'
>

type Props = {
  onClose: () => void
  customerTaxStatus?: 'TAXED' | 'TAX_EXEMPT' | 'REVERSE_CHARGED'
  customerCountry?: CountriesAlpha2
  customerState?: State
  taxCategories: TaxValidationTaxCategory[]
  recalculate: () => void | Promise<void>
}

export const useTaxRateValidation = ({
  onClose,
  customerTaxStatus,
  customerCountry,
  customerState,
  taxCategories,
  recalculate
}: Props) => {
  const { isExternalTaxIntegrationProvider } = useTaxIntegrationProvider()
  const {
    data: taxRatesData,
    isLoading: isTaxRatesLoading,
    hasNextPage,
    isError
  } = useLoadAllItemsQuery(
    dashboardv99990101Client.getTaxRates,
    // The API's largest page, so a big account needs fewer round trips.
    { limit: 100 },
    { enabled: !isExternalTaxIntegrationProvider }
  )
  // Invalidate so the paged tax-rate query refetches after new rates are
  // created — otherwise the missing-rate check below stays stale and the
  // remediation step keeps reporting the rates as missing.
  const createTaxRate = useCreateTaxRate({ invalidateQueries: true })
  const taxRates = useMemo(
    () => taxRatesData?.pages.flatMap(page => page.items) ?? [],
    [taxRatesData]
  )
  // Keep loading until every page is in. Checking early would report rates on
  // later pages as missing.
  const isLoading = isTaxRatesLoading || (hasNextPage && !isError)
  // The query stops at its page cap even when the API has more, so a cursor on
  // the last page means the list is cut short rather than complete.
  const lastPage = taxRatesData?.pages.at(-1)
  const isTaxRatesListTruncated =
    !hasNextPage && Boolean(lastPage?.pagination.after && lastPage.items.length)
  const isTaxRatesIncomplete = isError || isTaxRatesListTruncated
  const needsTaxRates =
    customerTaxStatus === 'TAXED' &&
    !isExternalTaxIntegrationProvider &&
    taxCategories.length > 0
  const isTaxRatesCheckFailed = needsTaxRates && isTaxRatesIncomplete

  const validation = useMemo<ValidationItem[]>(() => {
    // A partial list would prompt for rates that may already exist, and
    // saving them would create duplicates.
    if (isTaxRatesIncomplete) {
      return []
    }

    const taxRatesForCustomerCountry = taxRates.filter(
      taxRate => taxRate.country === customerCountry
    )
    const unconfiguredTaxCategories = taxCategories.filter(
      taxCategory =>
        !taxRatesForCustomerCountry.some(
          taxRate => taxRate.taxCategoryId === taxCategory.id
        )
    )
    const hasMissingTaxRates =
      needsTaxRates && unconfiguredTaxCategories.length > 0

    if (!hasMissingTaxRates) {
      return []
    }

    const componentProps: ComponentProps<typeof ControlledTaxRatesModal> = {
      'data-testid': 'addTaxRates',
      countriesWithTaxRates: {
        [customerCountry as CountriesAlpha2]: taxRatesForCustomerCountry
      } as Record<CountriesAlpha2, TaxRate[]>,
      preselectedCountry: customerCountry,
      preselectedState: customerState,
      taxCategories: unconfiguredTaxCategories,
      onSubmit: async values => {
        await createTaxRate(values)
        await recalculate()
      },
      onClose,
      options: {
        closeOnCancel: false,
        closeOnSubmit: false,
        submitText: 'Save tax rates',
        submittingText: 'Saving tax rates...'
      }
    }

    return [
      {
        component: ControlledTaxRatesModal,
        componentProps,
        message: getMissingTaxRateMessage({
          country: customerCountry,
          state: customerState,
          taxRates
        }),
        stepKey: 'TAX_RATES',
        stepName: 'Tax rates'
      } as ValidationItem
    ]
  }, [
    createTaxRate,
    customerCountry,
    customerState,
    isTaxRatesIncomplete,
    needsTaxRates,
    onClose,
    recalculate,
    taxCategories,
    taxRates
  ])

  return { isLoading, isTaxRatesCheckFailed, validation }
}
